import Box from "@mui/material/Box";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../../api/config/getPublicConfig";
import { getDossier } from "../../../api/dossier/getDossier";
import { getDossierInvoiceComparison } from "../../../api/dossier/getDossierInvoiceComparison";
import APSelectDropDown, {
  SelectDropdownOption,
} from "../../../components/APSelectDropDown";
import { APButtonLink } from "../../../components/buttons/APButtonLink";
import CRMButton from "../../../components/buttons/CRMButton";
import PageContainer from "../../../components/PageContainer";
import { getCRMClientURL } from "../../../helpers/configuration";
import { getAccountCRMUrl } from "../../../helpers/dossiers";
import { useAuth } from "../../../providers/AuthProvider";
import {
  filterInvoiceComparisons,
  InvoiceComparisonSortOption,
  sortInvoiceComparisons,
} from "../../../types/Document";
import APInvoiceComparisonTable from "./components/APInvoiceComparisonTable";

type invoiceComparisonFilters = {
  comparisonStatus: "matching" | "missing" | null;
};

const invoiceComparisonSortOptions: SelectDropdownOption<InvoiceComparisonSortOption>[] =
  [
    { id: "date", label: "Date" },
    { id: "status", label: "Status" },
  ];

const comparisonStatusOptions: SelectDropdownOption<
  "matching" | "missing" | "all"
>[] = [
  { id: "matching", label: "Matching" },
  { id: "missing", label: "Missing" },
  { id: "all", label: "All" },
];

const DossierInvoiceComparison = () => {
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const { dossierId } = useParams<{
    dossierId: string;
  }>();
  const [invoiceComparisonSortOptionId, setInvoiceComparisonSortOptionId] =
    useState<InvoiceComparisonSortOption>("status");
  const [invoiceComparisonFilters, setInvoiceComparisonFilters] =
    useState<invoiceComparisonFilters>({
      comparisonStatus: null,
    });

  const dossier = getDossier(dossierId ?? "");
  const config = getPublicConfig();

  const crmClientURL = config.isSuccess ? getCRMClientURL(config.data) : null;

  const {
    data: invoiceComparison,
    isLoading: isLoadingInvoiceComparison,
    isSuccess: isSuccessInvoiceComparison,
  } = getDossierInvoiceComparison(dossierId ?? "");

  const onInvoiceComparisonSortChange = (
    optionId: InvoiceComparisonSortOption
  ) => {
    setInvoiceComparisonSortOptionId(optionId);
  };

  const onComparisonStatusChange = (
    optionId: "matching" | "missing" | "all"
  ) => {
    setInvoiceComparisonFilters({
      ...invoiceComparisonFilters,
      comparisonStatus:
        optionId === "all" ? null : (optionId as "matching" | "missing"),
    });
  };

  const dropdowns = (
    <Box sx={{ my: 1, display: "flex", justifyContent: "start", columnGap: 1 }}>
      <Box sx={{ width: "168px", display: "flex" }}>
        <APSelectDropDown
          label="Sort By"
          selectedOptionId={invoiceComparisonSortOptionId}
          options={invoiceComparisonSortOptions}
          onOptionChange={onInvoiceComparisonSortChange}
        />
      </Box>

      <Box sx={{ width: "168px", display: "flex" }}>
        <APSelectDropDown
          label="Comparison Status"
          selectedOptionId={invoiceComparisonFilters.comparisonStatus ?? "all"}
          options={comparisonStatusOptions}
          onOptionChange={onComparisonStatusChange}
        />
      </Box>
    </Box>
  );

  const isLoading =
    isLoadingInvoiceComparison || dossier.isLoading || config.isLoading;
  const isSuccess =
    isSuccessInvoiceComparison && dossier.isSuccess && config.isSuccess;

  return (
    <PageContainer
      title="Invoice Comparison"
      loading={isLoading}
      size="full"
      titleButton={
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: 2 }}>
          <Box
            sx={{
              display: "flex",
              columnGap: 2,
              justifyContent: "end",
            }}
          >
            {isSuccess && dossier.data?.opportunity?.catchEContactUrl && (
              <>
                {crmClientURL && dossier.data?.opportunity?.parentAccountId && (
                  <Box>
                    <CRMButton
                      url={getAccountCRMUrl(
                        crmClientURL,
                        dossier.data.opportunity.parentAccountId
                      )}
                      sx={{ p: { sm: 1.5, xs: 1 } }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box>
            <APButtonLink
              text="Transaction Comparison"
              onClick={() =>
                navigate(
                  getScopedPageUrl(
                    "dossierTransactionComparison",
                    dossierId ?? ""
                  )
                )
              }
            />
          </Box>
        </Box>
      }
    >
      {isSuccess && (
        <Box mt={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>{dropdowns}</Box>
          </Box>
          <APInvoiceComparisonTable
            invoiceComparisons={sortInvoiceComparisons(
              filterInvoiceComparisons(
                invoiceComparison ?? [],
                invoiceComparisonFilters.comparisonStatus
              ),
              invoiceComparisonSortOptionId
            )}
            crmClientURL={crmClientURL}
          />
        </Box>
      )}
    </PageContainer>
  );
};

export default DossierInvoiceComparison;
